import { MediaType } from '@confluence/graphql/entry-points/MediaType';
import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation';
import { getEditDraftPageUrl } from '@confluence/graphql/entry-points/sharedTemplateBlueprintReqFns';

import type { CreatePageByTemplateMutation as PublishPageByTemplateMutation } from '../queries/__types__/CreatePageByTemplateMutation';

export const useExperimentalPublishPageByTemplateMutation = () => {
	return useExperimentalMutation<
		PublishPageByTemplateMutation,
		PublishPageByTemplateMutationVariables,
		PublishPageByTemplateMutationResponse
	>({
		url: ({ blueprint, attemptSpaUrlResponse }) => {
			const blueprintSpec = JSON.parse(blueprint);
			return `/wiki/rest/api/template/page/${blueprintSpec.templateId}/instance${
				attemptSpaUrlResponse ? '?expand=metadata.properties.editor,space' : ''
			}`;
		},
		method: 'POST',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: ({ spaceKey, contentId, blueprint, context, title, subType }) => {
			const blueprintSpec = JSON.parse(blueprint);
			const contextValue = context ? JSON.parse(context) : undefined;

			const commonContentProperties: any = {
				type: 'page',
				status: 'draft',
				space: {
					key: spaceKey,
				},
				title,
			};

			if (contentId && contentId !== 'no-parent-page') {
				commonContentProperties.ancestors = [{ id: contentId }];
			}

			if (subType) {
				commonContentProperties.subType = subType;
			}

			return JSON.stringify({
				content: {
					...commonContentProperties,
				},
				contentBlueprintSpec: {
					contentTemplateId: blueprintSpec.templateId,
					context: contextValue || {},
				},
			});
		},
		parseResult: async (json, { attemptSpaUrlResponse }) => {
			const editui = await getEditDraftPageUrl(json, attemptSpaUrlResponse);

			return {
				experimentalPublishPageByTemplate: {
					links: {
						draftUrl: editui,
					},
				},
			};
		},
	});
};

interface PublishPageByTemplateMutationVariables {
	spaceKey: string;
	blueprint: string;
	contentId?: string;
	context?: string;
	title?: string;
	subType?: string;
	attemptSpaUrlResponse?: boolean;
}

interface PublishPageByTemplateMutationResponse {
	_links?: {
		editui?: string;
	};
	metadata?: {
		properties?: {
			editor?: {
				value?: string;
			};
		};
	};
}
